import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { queryKeys } from "@/api/queries/keys";
import type { CartLinePriceListChange } from "@/api/types";
import { useDisplayMoney } from "@/lib/currency";
import { minorToMajor } from "@/lib/money";

export type PriceListChangeState = (CartLinePriceListChange & { lineId: number }) | null;

/**
 * KAN-1876 — „Ceník se od přidání změnil: 17 → 74 Kč/ks".
 *
 * Úprava řádku ho přecení AKTUÁLNÍM ceníkem (rozhodnutí vlastníka
 * 2026-10-05). Když se cena liší od zmrazené i mimo množstevní pásmo,
 * ``PATCH /cart/lines/{id}`` vrátí ``price_list_change`` a
 * ``useUpdateCartLine`` ho uloží sem — hláška pak nad košíkem řekne, že
 * nová cena není jen jiné pásmo. Na nic se neptá; zákazník ji zavře.
 */
export function PriceListChangeNotice({ locale }: { locale: string }): JSX.Element | null {
  const { t } = useTranslation("cart");
  const { format } = useDisplayMoney(locale);
  const qc = useQueryClient();
  const { data } = useQuery<PriceListChangeState>({
    queryKey: queryKeys.cartPriceListChange(),
    queryFn: () => null,
    enabled: false,
    initialData: null,
  });
  if (!data) return null;
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-warn-border bg-warn-bg px-4 py-3 text-sm text-warn"
      role="status"
      data-testid="cart-price-list-change"
    >
      <p className="min-w-0 flex-1">
        {t("priceListChange.notice", {
          previous: format(minorToMajor(data.previous_unit_minor, data.currency_code)),
          current: format(minorToMajor(data.current_unit_minor, data.currency_code)),
          defaultValue: "Ceník se od přidání změnil: {{previous}} → {{current}}/ks.",
        })}
      </p>
      <button
        type="button"
        className="text-xs underline"
        onClick={() => qc.setQueryData(queryKeys.cartPriceListChange(), null)}
        data-testid="cart-price-list-change-dismiss"
      >
        {t("priceListChange.dismiss", { defaultValue: "Rozumím" })}
      </button>
    </div>
  );
}
